<template>
  <v-container ref="container" style="max-width: 95%;">
    <h1>Financial Document Result: {{ uniqueId }}</h1>
    <v-simple-table
      :height="tableHeight"
      fixed-header
    >
      <template v-slot:default>
        <thead>
          <tr>
            <th v-for="(header, index) in headers" :key="index">
              {{ header.text }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(row, rowIndex) in displayData" :key="rowIndex">
            <td
              v-for="(cell, cellIndex) in row"
              :key="cellIndex"
              :rowspan="cell.rowspan || 1"
              :colspan="cell.colspan || 1"
              :style="getCellStyle(cell)"
              @click="openEditDialog('data_compare', rowIndex, cellIndex, cell)"
              style="cursor: pointer;"
            >
              {{ cell.value }}
            </td>
          </tr>
        </tbody>
      </template>
    </v-simple-table>

    <!-- Edit Dialog -->
    <v-dialog v-model="editDialog" max-width="400px">
      <v-card>
        <v-card-title>Edit Cell Value</v-card-title>
        <v-card-text>
          <v-form ref="editForm" @submit.prevent="updateValue">
            <v-text-field
              v-model="editCell.newValue"
              label="New Value"
              outlined
              dense
              :rules="[v => !!v || 'Value is required']"
              @keyup.enter="updateValue"
            ></v-text-field>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" text @click="closeDialog">Cancel</v-btn>
          <v-btn color="primary" @click="updateValue">Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import axios from 'axios'

export default {
  data() {
    return {
      taskId: this.$route.params.id,
      uniqueId: this.$route.params.uniqueId,
      loading: false,
      fieldDisplaySeq: [
        {},
        {name: "Revenue", field: ["income_statement", "revenue"]},
        {name: "COGS", field: ["income_statement", "cost_of_goods_sold"]},
        {name: "Gross Profit", field: ["income_statement", "gross_profit"]},
        {name: "Profit of the year", field: ["income_statement", "net_income"]},
        {},
        {name: "Cash Balance", field: ["current_asset", "cash_balance"]},
        {name: "Amount Due From Director", field: ["current_asset", "amount_due_from_director"]},
        {name: "Deposit and Prepayment", field: ["current_asset", "deposit_and_prepayment"]},
        {name: "Other Receivable", field: ["current_asset", "other_receivable"]},
        {name: "Inventory", field: ["current_asset", "inventory"]},
        {name: "Current Asset", field: ["current_asset", "current_asset_total"]},
        {},
        {name: "Bank Loan", field: ["current_liability", "bank_loan"]},
        {name: "Account Payable", field: ["current_liability", "account_payable"]},
        {name: "Amount Due To Director", field: ["current_liability", "amount_due_to_director"]},
        {name: "Current Liability", field: ["current_liability", "current_liability_total"]},
        {},
        {name: "Non Current Assets", field: ["balance_sheet", "non_current_asset_total"]},
        {name: "Non Current Liabilities", field: ["balance_sheet", "non_current_liability_total"]},
        {name: "Net Equity", field: ["balance_sheet", "equity"]},
      ],
      headers: [
        // { text: ''},
        // { text: ''},
        // { text: ''},
        // { text: ''},
      ],
      tableData: {},
      displayData: [],
      outputUrl: "",
      isCompleted: false,
      containerHeight: 0,
      // New data properties for editing
      editDialog: false,
      editCell: {
        tableType: '', // 'data_compare' or 'item_listing'
        groupIndex: null, // For item_listing, the group index
        rowIndex: null, // Row index within group (item_listing) or table (data_compare)
        cellIndex: null, // Cell index within row
        newValue: '' // The new value being edited
      }
    }
  },
  computed: {
    tableHeight() {
      const offset = 140;
      return `calc(100vh - ${offset}px)`;
    }
  },
  methods: {
    async fetchResult() {
      this.loading = true
      try {
        const response = await this.$http.get(`/api/tasks/${this.taskId}/result`)
        this.tableData = response.data
        this.convertToDisplayData()
        // this.initializeItemListing()
        this.isCompleted = true
      } catch (error) {
        console.error('Error fetching task result:', error)
        alert('Failed to load task result')
      }
      this.loading = false
    },
    getNestedValue(data, keyPath) {
      let curData = data
      for (const key of keyPath) {
        if (curData[key]) {
          curData = curData[key]
        }
        else {
          return null
        }
      }
      return curData
    },
    convertDisplayFormat(value, isPercentage=false, digits=2) {
      try {
        if (isPercentage) {
          value = value * 100
        }
        value = value.toLocaleString('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        })
        if (isPercentage) {
          value += "%"
        }        
      } catch (error) {
        // console.error(error);
      }
      return value
    },
    convertToDisplayData() {
      this.displayData = []
      this.headers = [{ text: '' }]

      const years = Object.keys(this.tableData)
        .sort((a, b) => {
          if (a > b) return -1;
          if (a < b) return 1;
          return 0;
        })

      // headers
      for (let year of years) {
        this.headers.push({ text: '' })
        this.headers.push({ text: year })
      }

      for (let displayInfo of this.fieldDisplaySeq) {
        let rowData = Array(this.headers.length)
        for (var i = 0; i < this.headers.length; i++) {
            rowData[i] = { value: "" };
        }
        if (displayInfo.name) {
          rowData[0].value = displayInfo.name
        }
        for (let i = 0; i < years.length; i++) {
          const rowIdx = 2 * (i + 1)
          const curYear = years[i]
          let curData = null
          if (displayInfo.field) {
            curData = this.getNestedValue(this.tableData[curYear], displayInfo.field)
            if (curData) {
              curData = Math.abs(parseFloat(curData.toString().replaceAll(',', '')))
              curData = isNaN(curData) ? "-" : curData
            }
            else {
              curData = "-"
            }
          }
          else if (displayInfo.constant) {
            curData = displayInfo.constant
          }
          rowData[rowIdx].value = this.convertDisplayFormat(curData, displayInfo.percentage)
          rowData[rowIdx].field = displayInfo.field
        }
        this.displayData.push(rowData)
      }
    },
    getCellStyle(cell) {
      const style = {
        backgroundColor: cell.highlight_color ? `#${cell.highlight_color}` : ''
      }
      if (cell.borders) {
        const styleMap = {
          thin: '1px solid',
          medium: '2px solid',
          thick: '3px solid'
        }
        const defaultBorderColor = '#000'
        if (cell.borders.left) {
          style.borderLeft = `${styleMap[cell.borders.left.style] || '1px solid'} ${
            cell.borders.left.color ? `#${cell.borders.left.color}` : defaultBorderColor
          }`
        }
        if (cell.borders.top) {
          style.borderTop = `${styleMap[cell.borders.top.style] || '1px solid'} ${
            cell.borders.top.color ? `#${cell.borders.top.color}` : defaultBorderColor
          }`
        }
        if (cell.borders.bottom) {
          style.borderBottom = `${styleMap[cell.borders.bottom.style] || '1px solid'} ${
            cell.borders.bottom.color ? `#${cell.borders.bottom.color}` : defaultBorderColor
          }`
        }
        if (cell.borders.right) {
          style.borderRight = `${styleMap[cell.borders.right.style] || '1px solid'} ${
            cell.borders.right.color ? `#${cell.borders.right.color}` : defaultBorderColor
          }`
        }
      }
      return style
    },
    updateContainerHeight() {
      this.containerHeight = this.$refs.container ? this.$refs.container.clientHeight : 0
    },
    openEditDialog(tableType, groupIndex, cellIndex, cell, rowIndex = null) {
      if (cell.value || cell.value === '-') {
        this.editCell = {
          tableType,
          cell,
          groupIndex,
          rowIndex: rowIndex !== null ? rowIndex : groupIndex, // For data_compare, groupIndex is rowIndex
          cellIndex,
          newValue: cell.value
        }
        this.editDialog = true
      }
    },
    // New method to update the cell value
    async updateValue() {
      try {
        const { tableType, cell, groupIndex, rowIndex, cellIndex, newValue } = this.editCell
        // Get the year from headers based on cellIndex
        const yearIndex = Math.floor((cellIndex - 1) / 2)
        const year = this.headers[cellIndex].text
        // Prepend "financial_document" and the year to the field array
        const updatedField = JSON.stringify(['financial_document', year, ...(cell.field || [])])
        await this.$http.post(`/api/tasks/${this.taskId}/update_value`, {
          field: updatedField,
          value: newValue
        })
        this.editDialog = false
        await this.fetchResult() // Refresh the table data
      } catch (error) {
        console.error('Error updating cell value:', error)
        alert('Failed to update cell value')
      }
    },
    // New method to handle dialog closing and form reset
    closeDialog() {
      this.editDialog = false
      if (this.$refs.editForm) {
        this.$refs.editForm.reset()
      }
    }
  },
  mounted() {
    this.fetchResult()
    this.outputUrl = `${this.$http.defaults.baseURL}/api/tasks/${this.$route.params.id}/output`
    // window.addEventListener('resize', this.updateContainerHeight)
    // this.updateContainerHeight()
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.updateContainerHeight)
  }
}
</script>

<style scoped>
::v-deep .v-data-table th:nth-child(1),
::v-deep .v-data-table td:nth-child(1) {
  width: 40% !important;
  max-width: 40% !important;
  text-align: left !important;
}

::v-deep .v-data-table th:nth-child(2),
::v-deep .v-data-table th:nth-child(3),
::v-deep .v-data-table th:nth-child(4) {}
::v-deep .v-data-table td:nth-child(2),
::v-deep .v-data-table td:nth-child(3),
::v-deep .v-data-table td:nth-child(4) {
  width: 20% !important;
  max-width: 20% !important;
}

::v-deep .v-data-table th {
  text-align: center !important;
}

::v-deep .v-data-table td {
  text-align: right !important;
}

td {
  border: 1px solid #ddd;
  padding: 8px;
}
td[style*="background-color"] {
  color: #fff;
}
::v-deep .v-data-table table {
  border-collapse: collapse;
}

</style>
